import { useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { displayValue, formatCompactCount, populatedFields, type SignalFacet } from '@bloo-team/api-client'
import { Button, Icon, Menu, Pagination } from '@bloo-team/ui'
import { ChevronDown, ChevronRight, Link, PanelsTopLeft, X } from '@bloo-team/ui/icons'
import { AiTriageCard } from './AiTriageCard'
import { KbContent } from './KbContent'
import { SignalTagChips } from './SignalTagChips'
import { EntityList, NotesAudit, PriorHandling, Section, StorylineMembers, useSignalDerived } from './SignalSections'
import { absTime } from '../../lib/local-time'
import { DISPOSITION_LABEL, signalFirstSeen, signalLastSeen, signalPrivate, signalSourceType, type SignalTriage } from '../../lib/signal-model'
import { agoLabel, copyText, signalLink } from './panel-utils'
import { SIGNAL_TABS, type SignalEventsView, type SignalEvidenceView, type SignalPanelProps, type SignalTab } from './types'

/**
 * The docked signal pane (docs/signals.md §The signal pane and panel) — one
 * scroll. Fixed on top: severity, status, copy link · full panel · close,
 * the title, the MITRE path, four facts (score · confidence · occurrences ·
 * detected) and Close / Suppress / More. Under it a sticky index of the
 * sections — Overview (reason, AI card, field breakdown, details) ·
 * Entities · Raw events · Knowledge base · Storyline · Activity — each one
 * collapsible. The page owns which are expanded: a collapsed section reads
 * nothing. No escalation from the pane.
 */

const STATUS_LABEL: Record<SignalTriage['status'], string> = { open: 'open', in_progress: 'in progress', closed: 'closed' }

const TAB_LABEL: Record<SignalTab, string> = {
  overview: 'Overview',
  entities: 'Entities',
  events: 'Raw events',
  kb: 'Knowledge base',
  storyline: 'Storyline',
  activity: 'Activity',
}

/** Sections expanded until the analyst changes them. */
export const DEFAULT_SECTIONS: ReadonlySet<SignalTab> = new Set<SignalTab>(['overview', 'entities'])

/** Values shown per field before the row expands (the legacy drawer showed three). */
export const FACET_TOP = 3

export function SignalPane(props: SignalPanelProps) {
  const {
    signal,
    triage,
    storyline,
    outsideTab,
    backToStoryline,
    tags,
    deepOpen,
    deep,
    suppressions,
    baseline,
    entityContext,
    suggestion,
    onAcceptSuggestion,
    onSuggestionNotHelpful,
    onOpenWorkbook,
    workbookBusy = false,
    workbookRefusal = null,
    onPromote,
    tab = 'overview',
    onTab,
    expanded = DEFAULT_SECTIONS,
    onToggleSection,
    kb,
    evidence,
    events,
    onToggleFalsePositive,
    falsePositiveBusy = false,
    onClose,
    onOpenEntity,
    onToggleDeep,
    onOpenAsHunt,
    onCloseSignal,
    onSuppress,
    onReopen,
    onAddNote,
    onOpenSearch,
    onOpenDetection,
    onSelectSignal,
  } = props
  const uid = useId()
  const derived = useSignalDerived(props)
  const [copied, setCopied] = useState<string | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  /** The index entry for the section in view — follows the scroll, never the URL. */
  const [inView, setInView] = useState<SignalTab>(tab)

  const status = triage?.status ?? 'open'
  const closed = status === 'closed'
  const attack = [signal.tactic, signal.technique, signal.subtechnique].filter(Boolean)
  const first = signalFirstSeen(signal)
  const last = signalLastSeen(signal)
  const sourceType = signalSourceType(signal)
  const isPrivate = signalPrivate(signal)
  const workbookName = String(signal.raw.$Workbook ?? signal.raw.Workbook ?? '') || null

  const sectionId = (t: SignalTab) => `${uid}-s-${t}`
  const scrollTo = (t: SignalTab) => {
    // After the expand renders; jsdom has no scrollIntoView.
    window.requestAnimationFrame(() => document.getElementById(sectionId(t))?.scrollIntoView?.({ block: 'start' }))
  }

  // `?ptab` anchors the pane: each signal opens scrolled to that section.
  const anchor = useRef(tab)
  useEffect(() => {
    anchor.current = tab
  })
  useEffect(() => {
    setInView(anchor.current)
    if (anchor.current !== 'overview') scrollTo(anchor.current)
    else bodyRef.current?.scrollTo?.({ top: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-anchor per signal only
  }, [signal.uuid])

  // The index highlights the section under the sticky index as the body scrolls.
  useEffect(() => {
    const root = bodyRef.current
    if (!root || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const t = (e.target as HTMLElement).dataset.section
          if (e.isIntersecting && t) setInView(t as SignalTab)
        }
      },
      { root, rootMargin: '-10% 0% -70% 0%', threshold: 0 },
    )
    for (const t of SIGNAL_TABS) {
      const el = document.getElementById(sectionId(t))
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- section ids are stable per mount
  }, [])

  const jump = (t: SignalTab) => {
    onTab?.(t)
    setInView(t)
    scrollTo(t)
  }

  const copyLink = () => {
    void copyText(signalLink(signal.uuid, window.location.href)).then((ok) => setCopied(ok ? 'Link copied' : 'Copy failed'))
    window.setTimeout(() => setCopied(null), 1600)
  }

  const count: Partial<Record<SignalTab, number>> = {
    entities: signal.entities.length,
    ...(events && events.total !== null && !events.loading ? { events: events.total } : {}),
    storyline: derived.memberCount,
  }

  const more = [
    { id: 'hunt', label: 'Open as hunt', hint: 'h', onAction: onOpenAsHunt },
    ...(onOpenWorkbook
      ? [{ id: 'workbook', label: workbookBusy ? 'Checking lock…' : 'Open workbook', isDisabled: workbookBusy || !signal.detectionId, onAction: onOpenWorkbook }]
      : []),
    { id: 'detection', label: 'Open detection', onAction: onOpenDetection },
    ...(onToggleFalsePositive
      ? [
          {
            id: 'fp',
            label: signal.fp ? 'Unmark false positive' : 'Mark as false positive',
            isDisabled: falsePositiveBusy,
            separatorBefore: true,
            onAction: onToggleFalsePositive,
          },
        ]
      : []),
  ]

  const details = (
    <Section eyebrow="details">
      <dl className="sp-facts">
        {signal.stage || tags?.length ? (
          <div className="sp-facts-wide">
            <dt>tags</dt>
            <dd className="hud-row sp-flags">
              {signal.stage ? (
                <span className="sg-stage" data-stage={signal.stage}>
                  {signal.stage}
                </span>
              ) : null}
              {tags?.length ? <SignalTagChips tags={tags} /> : null}
            </dd>
          </div>
        ) : null}
        <div className="sp-facts-wide">
          <dt>flags</dt>
          <dd className="hud-row sp-flags">
            <span className="sp-flag" data-on={signal.fp || undefined}>
              {signal.fp ? 'false positive' : 'not false positive'}
            </span>
            {isPrivate !== null ? (
              <span className="sp-flag" data-on={isPrivate || undefined}>
                {isPrivate ? 'private' : 'not private'}
              </span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>first seen</dt>
          <dd className="mono">{first !== null ? absTime(first) : '—'}</dd>
        </div>
        <div>
          <dt>last seen</dt>
          <dd className="mono">{last !== null ? absTime(last) : '—'}</dd>
        </div>
        <div>
          <dt>streams</dt>
          <dd className="mono" title={signal.streams.join(', ')}>
            {signal.streams.length ? signal.streams.join(', ') : '—'}
          </dd>
        </div>
        <div>
          <dt>type</dt>
          <dd>{sourceType || '—'}</dd>
        </div>
        <div className="sp-facts-wide">
          <dt>source id</dt>
          <dd className="mono" title={signal.detectionId ?? undefined}>
            {signal.detectionId ?? '—'}
          </dd>
        </div>
        {workbookName || onOpenWorkbook ? (
          <div className="sp-facts-wide">
            <dt>workbook</dt>
            <dd>
              {onOpenWorkbook && signal.detectionId ? (
                <button type="button" className="list-chip" onClick={onOpenWorkbook} disabled={workbookBusy}>
                  {workbookBusy ? 'checking lock…' : `${workbookName ?? 'open'} ↗`}
                </button>
              ) : (
                (workbookName ?? '—')
              )}
            </dd>
          </div>
        ) : null}
        {signal.remark ? (
          <div className="sp-facts-wide">
            <dt>remark</dt>
            <dd>{signal.remark}</dd>
          </div>
        ) : null}
        {triage?.huntId ? (
          <div className="sp-facts-wide">
            <dt>hunt</dt>
            <dd className="mono">hunt opened · {triage.huntId}</dd>
          </div>
        ) : null}
      </dl>
    </Section>
  )

  const body: Record<SignalTab, ReactNode> = {
    overview: (
      <>
        <Section eyebrow="why it fired">
          <p className="sp-reason">{derived.reason}</p>
        </Section>
        {suggestion ? (
          <AiTriageCard suggestion={suggestion} compact onAccept={onAcceptSuggestion} onNotHelpful={onSuggestionNotHelpful} />
        ) : null}
        <Section eyebrow="fields in the raw events">
          <FacetList evidence={evidence} />
        </Section>
        {details}
      </>
    ),
    entities: (
      <EntityList signal={signal} derived={derived} entityContext={entityContext} onOpenEntity={onOpenEntity} onOpenSearch={onOpenSearch} />
    ),
    events: (
      <>
        <EventList events={events} noQuery={evidence?.noQuery ?? false} />
        <div className="hud-row sp-aside">
          <Button variant={deepOpen ? 'outline' : 'solid'} size="sm" onPress={onToggleDeep}>
            {deepOpen ? 'Hide investigation' : 'Investigate'} <kbd className="sp-key">e</kbd>
          </Button>
          {signal.rawEventQuery ? (
            <Button variant="ghost" size="sm" tone="neutral" onPress={() => onOpenSearch(signal.rawEventQuery)}>
              Open in search
            </Button>
          ) : null}
        </div>
        {deepOpen && deep ? <div className="sp-deep">{deep}</div> : null}
      </>
    ),
    kb: <KnowledgeBase kb={kb} />,
    storyline: (
      <Section eyebrow={storyline ? storyline.title : 'same-entity signals'}>
        <StorylineMembers signal={signal} derived={derived} onSelectSignal={onSelectSignal} />
      </Section>
    ),
    activity: (
      <>
        <Section eyebrow={`prior handling · this detection · ${derived.poolLabel}`}>
          <PriorHandling derived={derived} suppressions={suppressions} baseline={baseline} />
        </Section>
        <Section eyebrow={`notes · audit · ${derived.audit.length}`}>
          {/* Keyed by signal: an unsent note draft never carries over to the next signal. */}
          <NotesAudit key={signal.uuid} derived={derived} onAddNote={onAddNote} />
        </Section>
      </>
    ),
  }

  return (
    <aside className="sg-pane sp-pane" aria-label="Signal" data-deep={deepOpen || undefined}>
      <div className="sp-pane-top">
        <div className="sp-pane-line">
          {backToStoryline ? (
            <button type="button" className="list-chip" title={backToStoryline.title} onClick={backToStoryline.onBack}>
              ← storyline
            </button>
          ) : null}
          <span className="sp-sev" data-severity={signal.severity} aria-hidden="true" />
          <span className="sp-sev-label">{signal.severity}</span>
          <span className="sp-status" data-status={status}>
            {STATUS_LABEL[status]}
            {triage?.disposition ? ` · ${DISPOSITION_LABEL[triage.disposition]}` : ''}
          </span>
          <span className="sp-pane-icons">
            {copied ? (
              <span className="hud-unit" role="status">
                {copied}
              </span>
            ) : null}
            <button type="button" className="qbc-x" aria-label="Copy signal link" title="Copy signal link" onClick={copyLink}>
              <Icon icon={Link} size={14} />
            </button>
            {onPromote ? (
              <button type="button" className="qbc-x" aria-label="Open full panel" title="Open full panel" onClick={onPromote}>
                <Icon icon={PanelsTopLeft} size={14} />
              </button>
            ) : null}
            <button type="button" className="qbc-x" aria-label="Close signal" title="Close" onClick={onClose}>
              <Icon icon={X} size={14} />
            </button>
          </span>
        </div>
        <h2 className="sp-pane-title">{signal.detectionName || 'Detection'}</h2>
        {outsideTab ? (
          <p className="hud-unit muted" role="note">
            not in this queue tab
          </p>
        ) : null}
        {attack.length ? (
          <p className="sp-mitre" aria-label="MITRE ATT&CK">
            {attack.map((a, i) => (
              <span key={a}>
                {i > 0 ? <span className="sp-chip-sep"> › </span> : null}
                {a}
              </span>
            ))}
          </p>
        ) : null}

        <dl className="sp-stats">
          <div className="sp-stat">
            <dt>score</dt>
            <dd>{signal.score !== null ? `${signal.score}/10` : '—'}</dd>
          </div>
          <div className="sp-stat">
            <dt>confidence</dt>
            <dd>{signal.confidence ?? '—'}</dd>
          </div>
          <div className="sp-stat">
            <dt>occurrences</dt>
            <dd title={String(signal.occurrences)}>{formatCompactCount(signal.occurrences)}</dd>
          </div>
          <div className="sp-stat">
            <dt>detected</dt>
            <dd title={absTime(signal.at)}>
              {absTime(signal.at)} <span className="hud-unit">{agoLabel(signal.at)}</span>
            </dd>
          </div>
        </dl>

        <div className="hud-row sp-pane-actions">
          {closed ? (
            <Button variant="outline" size="sm" tone="neutral" onPress={onReopen}>
              Reopen
            </Button>
          ) : (
            <Button variant="solid" size="sm" onPress={onCloseSignal}>
              Close… <kbd className="sp-key">x</kbd>
            </Button>
          )}
          <Button variant="outline" size="sm" tone="neutral" onPress={onSuppress}>
            Suppress… <kbd className="sp-key">s</kbd>
          </Button>
          <Menu
            trigger={
              <Button variant="ghost" size="sm" tone="neutral">
                More <Icon icon={ChevronDown} size={12} />
              </Button>
            }
            items={more}
            placement="bottom end"
          />
        </div>
        {workbookRefusal ? (
          <div className="bui-alert bui-alert-error sg-lock-msg" role="alert">
            <span className="bui-alert-mark">✕</span>
            <span>
              <span className="bui-alert-title">Workbook not opened</span>
              {workbookRefusal}
            </span>
          </div>
        ) : null}
      </div>

      <nav className="sp-index" aria-label="Signal sections">
        {SIGNAL_TABS.map((t) => (
          <button key={t} type="button" className="sp-index-item" aria-current={inView === t ? 'true' : undefined} onClick={() => jump(t)}>
            {TAB_LABEL[t]}
            {count[t] !== undefined ? <span className="sg-queue-n">{count[t]}</span> : null}
          </button>
        ))}
      </nav>

      <div ref={bodyRef} className="sg-pane-body sp-pane-body">
        {SIGNAL_TABS.map((t) => {
          const open = expanded.has(t)
          return (
            <section key={t} id={sectionId(t)} data-section={t} className="sp-part" aria-labelledby={`${uid}-h-${t}`}>
              <h3 className="sp-part-head">
                <button
                  type="button"
                  id={`${uid}-h-${t}`}
                  className="sp-part-toggle"
                  aria-expanded={open}
                  aria-controls={`${uid}-b-${t}`}
                  onClick={() => onToggleSection?.(t)}
                >
                  <Icon icon={open ? ChevronDown : ChevronRight} size={12} />
                  {TAB_LABEL[t]}
                  {count[t] !== undefined ? <span className="sg-queue-n">{count[t]}</span> : null}
                </button>
              </h3>
              <div id={`${uid}-b-${t}`} className="sp-part-body" hidden={!open}>
                {open ? body[t] : null}
              </div>
            </section>
          )
        })}
      </div>
    </aside>
  )
}


/* ------------------------------------------------------------ field facets */

function FacetList({ evidence }: { evidence?: SignalEvidenceView }) {
  if (!evidence || evidence.loading) return <p className="q-facet-note">Reading the signal's raw events…</p>
  if (evidence.error) {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>
          <span className="bui-alert-title">Field breakdown failed</span>
          {evidence.error}{' '}
          <button type="button" className="list-chip" onClick={evidence.onRetry}>
            Retry
          </button>
        </span>
      </div>
    )
  }
  if (evidence.noQuery) return <p className="q-facet-note">This signal has no raw-event query, so there is nothing to break down.</p>
  if (!evidence.facets.length) return <p className="q-facet-note">No fields in the raw events.</p>
  return (
    <ul className="sp-facets">
      {evidence.facets.map((f) => (
        <FacetRow key={f.field} facet={f} />
      ))}
    </ul>
  )
}

function FacetRow({ facet }: { facet: SignalFacet }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const total = facet.values.reduce((n, v) => n + v.count, 0) || 1
  const extra = facet.values.length - FACET_TOP
  const shown = open ? facet.values : facet.values.slice(0, FACET_TOP)
  return (
    <li className="sp-facet">
      <span className="sp-facet-name">
        {facet.field}
        <span className="hud-unit">{facet.distinct} unique</span>
      </span>
      <ul className="sp-facet-vals" id={id}>
        {shown.map((v) => (
          <li key={v.value} className="sp-facet-val">
            <span className="sp-facet-bar mono" title={v.value} style={{ ['--w' as string]: `${Math.round((v.count / total) * 100)}%` }}>
              <span>{v.value}</span>
            </span>
            <span className="sp-facet-n mono">{v.count}</span>
          </li>
        ))}
      </ul>
      {extra > 0 ? (
        <button type="button" className="list-chip sp-facet-more" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
          {open ? 'fewer' : `+${extra} more`}
        </button>
      ) : null}
    </li>
  )
}

/* -------------------------------------------------------------- raw events */

function EventList({ events, noQuery }: { events?: SignalEventsView; noQuery: boolean }) {
  if (noQuery) return <p className="q-facet-note">This signal has no raw-event query.</p>
  if (!events || (events.loading && !events.rows.length)) return <p className="q-facet-note">Reading the signal's raw events…</p>
  if (events.error) {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>
          <span className="bui-alert-title">Raw events failed</span>
          {events.error}
        </span>
      </div>
    )
  }
  if (!events.rows.length) return <p className="q-facet-note">No raw events.</p>
  const cols = populatedFields(events.fields.length ? events.fields : Object.keys(events.rows[0] ?? {}), events.rows).slice(0, 3)
  return (
    <>
      <ol className="sp-events">
        {events.rows.map((r, i) => (
          <EventRow key={`${events.pageNo}-${i}`} row={r} cols={cols} />
        ))}
      </ol>
      {events.total !== null && events.total > events.pageSize ? (
        <Pagination page={events.pageNo} pageSize={events.pageSize} total={events.total} onPageChange={events.onPage} busy={events.loading} label="Raw events pages" />
      ) : null}
    </>
  )
}

function EventRow({ row, cols }: { row: Record<string, unknown>; cols: string[] }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <li className="sp-event">
      <button type="button" className="sp-event-row" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        <Icon icon={open ? ChevronDown : ChevronRight} size={12} />
        <span className="sp-event-cells mono">{cols.map((c) => displayValue(c, row[c])).join(' · ')}</span>
      </button>
      {open ? (
        <pre id={id} className="sp-json">
          {JSON.stringify(row, null, 2)}
        </pre>
      ) : null}
    </li>
  )
}

/* ---------------------------------------------------------- knowledge base */

function KnowledgeBase({ kb }: { kb?: SignalPanelProps['kb'] }) {
  if (!kb || kb.loading) return <p className="q-facet-note">Loading the knowledge base…</p>
  if (kb.error) {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>
          <span className="bui-alert-title">Knowledge base failed</span>
          {kb.error}
        </span>
      </div>
    )
  }
  if (!kb.text.trim()) return <p className="q-facet-note">No knowledge base for this detection.</p>
  return (
    <div className="sp-kb">
      <KbContent text={kb.text} />
    </div>
  )
}
